import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { Megaphone, Send, Trash2, Mail, Users, Loader2, Clock, Save, FileText, User } from "lucide-react";
import { PromptDialog } from "@/components/PromptDialog";

const TEMPLATE = `We're letting you know about a scheduled maintenance / update.

What's changing: [brief description]
When: [date & time, your timezone]
Impact: [e.g. no downtime expected; you may be asked to sign in again]
What you need to do: [nothing / sign back in / etc.]

Thank you,
ATF WebWorks · Ask Top Foster`;

const GROUP_LABELS = { subscribers: "Subscription clients", concierge: "AI Concierge clients", business_plan: "Business Plan clients" };
const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";
const SELECT = "bg-navy border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none";

export function Announcements() {
  const [list, setList] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);

  // Audience
  const [audiences, setAudiences] = useState(null); // {all, groups:{...}}
  const [clients, setClients] = useState([]);
  const [audSel, setAudSel] = useState("all"); // all | group:xxx | client
  const [clientId, setClientId] = useState("");

  // Templates
  const [templates, setTemplates] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/announcements").then((r) => setList(r.data)).catch(() => setList([]));
    api.get("/admin/announcement-audiences").then((r) => setAudiences(r.data)).catch(() => {});
    api.get("/admin/announcement-clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/admin/announcement-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const audiencePayload = () => {
    if (audSel === "all") return { audience_type: "all" };
    if (audSel === "client") return { audience_type: "client", audience_client_id: clientId };
    if (audSel.startsWith("group:")) return { audience_type: "group", audience_group: audSel.split(":")[1] };
    return { audience_type: "all" };
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and a message."); return; }
    if (audSel === "client" && !clientId) { toast.error("Pick which client to send to."); return; }
    let scheduled_for = null;
    if (scheduleOn) {
      if (!scheduleAt) { toast.error("Pick a date and time to schedule."); return; }
      const when = new Date(scheduleAt);
      if (isNaN(when.getTime()) || when <= new Date()) { toast.error("Scheduled time must be in the future."); return; }
      scheduled_for = when.toISOString();
    } else if (sendEmail && !window.confirm("Send this announcement now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/announcements", {
        subject: subject.trim(), body: body.trim(), send_email: sendEmail, scheduled_for, ...audiencePayload(),
      });
      if (scheduled_for) toast.success(`Scheduled for ${new Date(scheduled_for).toLocaleString()}.`);
      else toast.success(sendEmail ? `Posted to the portal and emailed ${data.emailed} of ${data.recipients} recipient(s).` : "Posted to the portal.");
      setSubject(""); setBody(""); setScheduleOn(false); setScheduleAt("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement? It will be removed from client portals (or cancelled if scheduled).")) return;
    try { await api.delete(`/admin/announcements/${id}`); load(); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [showTplPrompt, setShowTplPrompt] = useState(false);
  const saveTemplate = () => {
    if (!subject.trim() && !body.trim()) { toast.error("Write a subject or message first."); return; }
    setShowTplPrompt(true);
  };
  const doSaveTemplate = async (name) => {
    setShowTplPrompt(false);
    try {
      await api.post("/admin/announcement-templates", { name: name.trim(), subject: subject.trim(), body: body.trim() });
      toast.success("Template saved.");
      api.get("/admin/announcement-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || ""); setBody(t.body || "");
    toast.success(`Loaded "${t.name}".`);
  };

  const delTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/admin/announcement-templates/${id}`); setTemplates((t) => t.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const recipientCount = () => {
    if (!audiences) return null;
    if (audSel === "all") return audiences.all;
    if (audSel === "client") return clientId ? 1 : 0;
    if (audSel.startsWith("group:")) return audiences.groups?.[audSel.split(":")[1]] ?? 0;
    return null;
  };
  const rc = recipientCount();

  return (
    <div data-testid="admin-announcements">
      <Head title="Announcements" desc="Send a maintenance or update notice to clients — posted in their portal and (optionally) emailed. Target everyone, a group, or one client. Send now or schedule for later." />

      <div className="bg-navy-800 border border-white/10 p-6 mb-8">
        <div className="flex items-center gap-2 text-gold mb-4"><Megaphone size={18} /><span className="font-heading text-lg text-white">New announcement</span></div>
        <div className="space-y-4">
          {/* Templates row */}
          <div className="flex flex-wrap items-center gap-3">
            <FileText size={15} className="text-gold" />
            <select value="" onChange={(e) => loadTemplate(e.target.value)} data-testid="announcement-template-load" className={SELECT}>
              <option value="">Load a saved template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button type="button" onClick={() => setBody(TEMPLATE)} data-testid="announcement-template" className="text-xs text-gold hover:underline">Insert maintenance template</button>
            {templates.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 ml-1">
                {templates.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 text-[11px] bg-navy border border-white/10 px-2 py-1 text-gray-300" data-testid={`announcement-template-chip-${t.id}`}>
                    {t.name}
                    <button onClick={() => delTemplate(t.id)} data-testid={`announcement-template-del-${t.id}`} className="text-gray-500 hover:text-red-400"><Trash2 size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <input data-testid="announcement-subject" className={INPUT} placeholder="Subject — e.g. Scheduled maintenance, Sat Aug 8" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <textarea data-testid="announcement-body" rows={9} className={INPUT} placeholder="Your message to clients…" value={body} onChange={(e) => setBody(e.target.value)} />

          {/* Audience row */}
          <div className="flex flex-wrap items-center gap-3">
            <Users size={15} className="text-gold" />
            <span className="text-sm text-gray-400">Send to</span>
            <select value={audSel} onChange={(e) => setAudSel(e.target.value)} data-testid="announcement-audience" className={SELECT}>
              <option value="all">Everyone{audiences ? ` (${audiences.all})` : ""}</option>
              <option value="group:subscribers">Subscription clients{audiences ? ` (${audiences.groups?.subscribers ?? 0})` : ""}</option>
              <option value="group:concierge">AI Concierge clients{audiences ? ` (${audiences.groups?.concierge ?? 0})` : ""}</option>
              <option value="group:business_plan">Business Plan clients{audiences ? ` (${audiences.groups?.business_plan ?? 0})` : ""}</option>
              <option value="client">A specific client…</option>
            </select>
            {audSel === "client" && (
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="announcement-client" className={SELECT}>
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name && c.name !== c.email ? `${c.name} · ${c.email}` : c.email}</option>)}
              </select>
            )}
            {rc != null && <span className="text-xs text-gray-500" data-testid="announcement-recipient-count">{rc} recipient{rc === 1 ? "" : "s"}</span>}
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="announcement-send-email" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-gold" />
              <Mail size={14} className="text-gold" /> Also email
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="announcement-schedule-toggle" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} className="accent-gold" />
              <Clock size={14} className="text-gold" /> Schedule for later
            </label>
            {scheduleOn && (
              <input type="datetime-local" data-testid="announcement-schedule-at" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                className="bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            )}
            <button type="button" onClick={saveTemplate} data-testid="announcement-save-template" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-gold transition-colors">
              <Save size={14} /> Save as template
            </button>
            <button onClick={send} disabled={sending} data-testid="announcement-send"
              className="ml-auto inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={16} /> : (scheduleOn ? <Clock size={16} /> : <Send size={16} />)} {scheduleOn ? "Schedule" : (sendEmail ? "Post & email" : "Post to portal")}
            </button>
          </div>
        </div>
      </div>

      <h3 className="font-heading text-lg text-white mb-4">Sent &amp; scheduled announcements</h3>
      {list === null ? <Loader /> : list.length === 0 ? <EmptyBox text="No announcements yet." /> : (
        <div className="space-y-3">
          {list.map((a) => {
            const isScheduled = !a.published_at && a.scheduled_for;
            return (
            <div key={a.id} className="bg-navy-800 border border-white/10 p-5" data-testid={`announcement-row-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold">{a.subject}</span>
                    {isScheduled && <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-gold px-2 py-0.5" data-testid={`announcement-scheduled-${a.id}`}>Scheduled</span>}
                    {a.audience_type && a.audience_type !== "all" && <span className="text-[10px] uppercase tracking-wider bg-white/10 text-gray-300 px-2 py-0.5 inline-flex items-center gap-1"><User size={10} /> {a.audience_label || GROUP_LABELS[a.audience_group] || "Targeted"}</span>}
                  </div>
                  <div className="text-gray-400 text-sm mt-1 whitespace-pre-wrap line-clamp-4">{a.body}</div>
                </div>
                <button onClick={() => remove(a.id)} data-testid={`announcement-delete-${a.id}`} className="text-gray-500 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-gray-500">
                {isScheduled
                  ? <span className="flex items-center gap-1 text-gold"><Clock size={12} /> Goes out {new Date(a.scheduled_for).toLocaleString()}</span>
                  : <>
                      <span>{timeAgo(a.created_at)}</span>
                      {a.emailed_at
                        ? <span className="flex items-center gap-1 text-gold"><Users size={12} /> Emailed {a.emailed}/{a.recipients}</span>
                        : <span className="flex items-center gap-1"><Users size={12} /> Portal only ({a.recipients})</span>}
                    </>}
              </div>
            </div>
          );})}
        </div>
      )}
      <PromptDialog open={showTplPrompt} title="Save as template" label="Template name"
        placeholder="e.g. Monthly maintenance" confirmText="Save template"
        onSubmit={doSaveTemplate} onClose={() => setShowTplPrompt(false)} />
    </div>
  );
}
